import React, { useState, useEffect } from 'react';
import { Avatar, AvatarProps } from '@mui/material';
import residentService from '../services/residentService';

interface ResidentAvatarProps extends Omit<AvatarProps, 'src'> {
    residentId?: string;
    residentName?: string;
    refreshTrigger?: number;
    fallbackUrl?: string;
}

const avatarCache: Record<string, Promise<string | null>> = {};

const ResidentAvatar: React.FC<ResidentAvatarProps> = ({
    residentId,
    residentName,
    refreshTrigger,
    fallbackUrl,
    children,
    ...props
}) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!residentId) {
            setBlobUrl(null);
            return;
        }

        let isMounted = true;

        const fetchAvatar = async () => {
            const cacheKey = `${residentId}_${refreshTrigger || 0}`;
            
            if (!avatarCache[cacheKey]) {
                avatarCache[cacheKey] = (async () => {
                    try {
                        const blob = await residentService.getProfilePicture(residentId);
                        return URL.createObjectURL(blob);
                    } catch (err) {
                        console.warn(`Failed to fetch authenticated avatar for resident ${residentId}`);
                        return null;
                    }
                })();
            }

            const url = await avatarCache[cacheKey];
            if (isMounted) {
                setBlobUrl(url);
            }
        };

        fetchAvatar();

        return () => {
            isMounted = false;
        };
    }, [residentId, refreshTrigger]);

    const initials = residentName
        ? residentName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
        : children;

    return (
        <Avatar src={blobUrl || fallbackUrl} {...props}>
            {initials}
        </Avatar>
    );
};

export default ResidentAvatar;
